/**
 * `Intl` formatters, built once per locale and shape instead of once per call.
 *
 * Constructing an `Intl` formatter is the expensive half of using one: it resolves the locale,
 * loads the data for it and compiles the pattern, and `format()` on the result is comparatively
 * free. That is why the platform hands back an OBJECT rather than a function — reusing it is the
 * intended way to use the API.
 *
 * It matters here because the roster builds them in a loop. Every visible row formats a sleep mean,
 * a mood mean and a relative date, and `RosterTable` re-renders the whole body on `onMouseMove`, so
 * a nine-row table constructed dozens of formatters per pointer move. Nothing about the formatter
 * differs between rows: the locale is the table's and the options are literals at the call site.
 *
 * ── Why a cache and not a module-level `const` ────────────────────────────────────────────────
 * The locale is not known at module load. It comes from i18next and changes when the trainer
 * switches language, so a formatter built at import time would print Polish numerals to an English
 * reader for the rest of the session. Keyed by locale, both languages coexist and neither is stale.
 *
 * The key includes the OPTIONS, because two call sites asking the same locale for different
 * fraction digits are asking for two different formatters. Both parts of the key come from literals
 * in this app's own code — there is no user input on this path — so the map is bounded by the
 * number of call sites times the number of languages, not by anything a client can grow.
 *
 * Only reach for this where a formatter is built per ROW or per CELL. A projection that builds one
 * formatter and folds a whole list with it is already doing the right thing.
 */

/**
 * A memoized constructor, keyed by locale and options.
 *
 * One body for all three: the map, the key and the "built at most once" are the whole of this
 * module, and three verbatim copies of them is the near-duplicate the house rule names by hand.
 * Each cache is the closure's own, so two formatter families never share a key space.
 */
function memoized<O extends object, F>(make: (locale: string, options: O) => F) {
  const cache = new Map<string, F>();
  return (locale: string, options: O): F => {
    const key = `${locale}|${JSON.stringify(options)}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;

    const made = make(locale, options);
    cache.set(key, made);
    return made;
  };
}

/** An `Intl.NumberFormat` for this locale and these options, built at most once. */
export const numberFormat = memoized(
  (locale: string, options: Intl.NumberFormatOptions) => new Intl.NumberFormat(locale, options),
);

/** An `Intl.RelativeTimeFormat` for this locale and these options, built at most once. */
export const relativeTimeFormat = memoized(
  (locale: string, options: Intl.RelativeTimeFormatOptions) =>
    new Intl.RelativeTimeFormat(locale, options),
);

/**
 * An `Intl.DateTimeFormat` for this locale and these options, built at most once.
 *
 * The third formatter to earn a place here, on the same evidence as the first two: the trainer's
 * home words a date inside every task row's sentence and a clock inside every activity row, so a
 * fresh formatter per row is exactly the construction this module exists to stop.
 *
 * Nothing here ports the mobile app's static PL/EN date tables. Those exist because Hermes throws
 * on `Intl.DateTimeFormat` with a `timeZone`; a browser has the whole of ECMA-402.
 */
export const dateTimeFormat = memoized(
  (locale: string, options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, options),
);
